import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router";
import { compose } from "recompose";
import { RMACancelErrorI18n } from "~/components/RMAForm/RMACancelError";
import RMAForm from "~/components/RMAForm/RMAForm";
import { IRMAOrderItem } from "~/components/RMAForm/RMAProductSelector";
import { nexusRmaTemplate } from "~/config/nexus_rma_template";
import { rmaTemplate } from "~/config/rma_template";
import {
  REASONS_REQUIRING_FILE_UPLOAD,
  REASONS_REQUIRING_ORDER_NUMBER
} from "~/config/support_reasons";
import client from "~/graphql/client";
import {
  FormStatus,
  ICreateTicketProps,
  OrderNumberValidationState,
  TicketType,
  withCreateTicketMutation
} from "~/graphql/mutations/trengoTicket/createTrengoTicket";
import {
  IStagedUploadInput,
  IStagedUploadsCreateProps,
  IStagedUploadsCreateResponse,
  StagedUploadType,
  withStagedUploadsCreateMutation
} from "~/graphql/mutations/trengoTicket/uploadFile";
import nexusClient, {
  IOrder as INexusOrder,
  IOrderByName,
  ORDER_LINE_ITEMS_QUERY
} from "~/graphql/queries/order/orderByName";
import {
  IOrder,
  ORDER_QUERY,
  orderByOrderNumberAndPostalCode,
  orderByOrderNumberAndPostalCodeVariables
} from "~/graphql/queries/order/orderByNumberAndPostalCode";
import cleanup from "~/helpers/cleanup";
import { invariant } from "~/helpers/invariant";
import isNexusPlatform from "~/helpers/isNexusPlatform";

export interface ITrengoTicket {
  description: string;
  email: string;
  name: string;
  orderNumber: string;
  phone: string;
  reason: string;
  subject: string;
  postalCode: string;
  document: File | undefined;
}

type Topic = [string, string];

interface IState {
  errorCode?: string;
  status: FormStatus;
  trengoTicket: ITrengoTicket;
  orderNumberValidationState: OrderNumberValidationState;
  rmaOrderItems: IRMAOrderItem[];
  order: IOrder | undefined;
  nexusOrder: INexusOrder | undefined;
}

interface IProps {
  reason?: string;
  orderNumber?: string;
}

class RMAFormContainer extends React.Component<
  IProps &
    ICreateTicketProps &
    IStagedUploadsCreateProps &
    WithTranslation &
    RouteComponentProps<{}>,
  IState
> {
  public static getDerivedStateFromProps(
    props: IProps & ICreateTicketProps & IStagedUploadsCreateProps,
    state: IState
  ): IState {
    const { trengoTicket } = state;

    return {
      ...state,
      trengoTicket: {
        ...trengoTicket,
        description:
          (trengoTicket.description !== "" && trengoTicket.description) || "",
        orderNumber:
          (trengoTicket.orderNumber !== "" && trengoTicket.orderNumber) ||
          props.orderNumber ||
          "",
        reason:
          (trengoTicket.reason !== "" && trengoTicket.reason) ||
          props.reason ||
          ""
      }
    };
  }

  public state: IState = {
    nexusOrder: undefined,
    order: undefined,
    orderNumberValidationState: OrderNumberValidationState.DEFAULT,
    rmaOrderItems: [],
    status: FormStatus.FORM,
    trengoTicket: {
      description: "",
      document: undefined,
      email: "",
      name: "",
      orderNumber: "",
      phone: "",
      postalCode: "",
      reason: "",
      subject: ""
    }
  };

  public render() {
    const {
      trengoTicket,
      errorCode,
      status,
      orderNumberValidationState,
      order,
      nexusOrder,
      rmaOrderItems
    } = this.state;

    const { postalCode } = trengoTicket;

    if (trengoTicket.reason === "cancel_order") {
      return <RMACancelErrorI18n />;
    }

    switch (status) {
      case FormStatus.FORM:
      case FormStatus.FORM_LOADING:
        return (
          <RMAForm
            order={order}
            nexusOrder={nexusOrder}
            loading={status === FormStatus.FORM_LOADING}
            errorCode={errorCode}
            trengoTicket={trengoTicket}
            orderNumberValidationState={orderNumberValidationState}
            rmaOrderItems={rmaOrderItems}
            postalCode={postalCode}
            onSubmit={this.handleSubmit}
            onChange={this.handleChange}
            onPostalCodeChange={this.handlePostalCodeChange}
            onValidateOrderNumber={this.handleValidateOrderNumber}
            onProductSelectorChange={this.handleProductSelectorChange}
          />
        );
    }
  }

  public componentDidMount() {
    this.setState(state => {
      return {
        trengoTicket: {
          ...state.trengoTicket,
          ...this.getSubjectForReason(state.trengoTicket.reason)
        }
      };
    });
  }

  public componentDidUpdate(prevProps: IProps, prevState: IState) {
    if (prevState.trengoTicket.reason === this.state.trengoTicket.reason) {
      return;
    }

    this.setState(state => {
      return {
        trengoTicket: {
          ...state.trengoTicket,
          ...this.getSubjectForReason(state.trengoTicket.reason)
        }
      };
    });
  }

  private handleProductSelectorChange = (rmaOrderItems: IRMAOrderItem[]) => {
    this.setState({ rmaOrderItems });
  };

  private handleValidateOrderNumber = async () => {
    const {
      trengoTicket: { orderNumber, postalCode },
      order: previousOrder,
      nexusOrder: previousNexusOrder
    } = this.state;

    if (!orderNumber || !postalCode) {
      return;
    }

    this.setState({
      orderNumberValidationState: OrderNumberValidationState.LOADING
    });

    if (isNexusPlatform) {
      try {
        const {
          data: { orderByName: nexusOrder }
        } = await nexusClient.query<IOrderByName>({
          fetchPolicy: "network-only",
          query: ORDER_LINE_ITEMS_QUERY,
          variables: {
            name: orderNumber,
            zip: postalCode
          }
        });

        if (nexusOrder) {
          // Ensure we just loaded a new or different order than the one we had
          if (
            !previousNexusOrder ||
            nexusOrder.name !== previousNexusOrder.name
          ) {
            const rmaOrderItems = nexusOrder.orderLineItems.edges.map(
              ({ node: orderLineItem }) => ({
                barcode: orderLineItem.barcode,
                orderItemId: orderLineItem.id,
                quantity: 0
              })
            );

            this.setState({
              nexusOrder,
              orderNumberValidationState: OrderNumberValidationState.VALID,
              rmaOrderItems
            });
          } else if (previousNexusOrder) {
            this.setState({
              orderNumberValidationState: OrderNumberValidationState.VALID
            });
          }
        }
      } catch {
        this.setState({
          order: undefined,
          orderNumberValidationState: OrderNumberValidationState.INVALID
        });
      }
    } else {
      const {
        data: { order }
      } = await client.query<
        orderByOrderNumberAndPostalCode,
        orderByOrderNumberAndPostalCodeVariables
      >({
        query: ORDER_QUERY,
        variables: {
          languageCode:
            this.props.t("translationLanguageCode").toString() || "en",
          orderNumber: cleanup(orderNumber),
          postalCode: cleanup(postalCode)
        }
      });

      if (order) {
        // Ensure we just loaded a new or different order than the one we had
        if (!previousOrder || order.number !== previousOrder.number) {
          const rmaOrderItems = order.orderItems.map<IRMAOrderItem>(
            orderItem => ({
              orderItemId: orderItem.id,
              quantity: 0
            })
          );

          this.setState({
            order,
            orderNumberValidationState: OrderNumberValidationState.VALID,
            rmaOrderItems
          });
        } else if (previousOrder) {
          this.setState({
            orderNumberValidationState: OrderNumberValidationState.VALID
          });
        }
      } else {
        this.setState({
          order: undefined,
          orderNumberValidationState: OrderNumberValidationState.INVALID
        });
      }
    }
  };

  private fileToStagedUploadParams = (file: File): IStagedUploadInput => ({
    fileSize: file.size,
    filename: file.name,
    mimeType: file.type,
    type: StagedUploadType.Ticket
  });

  private uploadDocuments = async (documents: File[]) => {
    if (documents.length === 0) {
      return [];
    }

    const stagedUploadParams = documents.map(this.fileToStagedUploadParams);

    const response = ((await this.props.uploadFile({
      variables: { input: { stagedUploads: stagedUploadParams } }
    })) as unknown) as IStagedUploadsCreateResponse;

    if (
      response.data.stagedUploadsCreate.__typename !==
      "StagedUploadsCreatePayload"
    ) {
      return null;
    }

    const stagedTargets = response.data
      ? response.data.stagedUploadsCreate.stagedTargets
      : [];

    stagedTargets.map(async (stagedTarget, index) => {
      const formData = new FormData();
      stagedTarget.parameters.forEach(
        ({ name, value }: { name: string; value: string }) =>
          formData.append(name, value)
      );
      formData.append("file", documents[index]);

      invariant(stagedTarget !== undefined, "Staged target is unavailable");

      try {
        await fetch(stagedTarget.url, {
          body: formData,
          credentials: "omit",
          headers: { "content-encoding": "multipart/form-data" },
          method: "POST",
          mode: "no-cors"
        });
      } catch (error) {
        throw new Error(error.message ? error.message : "Something went wrong");
      }
    });

    return stagedTargets.map((stagedTarget, index) => ({
      stagedUploadId: stagedTarget.stagedUploadId
    }));
  };

  private handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    const { trengoTicket, nexusOrder, order, rmaOrderItems } = this.state;

    if (
      REASONS_REQUIRING_FILE_UPLOAD.includes(trengoTicket.reason) &&
      !trengoTicket.document
    ) {
      this.setState({ errorCode: "enterRequiredFields" });

      return;
    }

    if (
      REASONS_REQUIRING_ORDER_NUMBER.includes(trengoTicket.reason) &&
      !trengoTicket.orderNumber
    ) {
      this.setState({ errorCode: "enterRequiredFields" });

      return;
    }

    if (
      !rmaOrderItems.length ||
      rmaOrderItems.reduce((acc, item) => acc + item.quantity, 0) === 0
    ) {
      this.setState({ errorCode: "requiredQuantity" });

      return;
    }

    if (
      !(
        trengoTicket.name &&
        trengoTicket.email &&
        trengoTicket.reason &&
        trengoTicket.description &&
        trengoTicket.phone
      )
    ) {
      this.setState({ errorCode: "enterRequiredFields" });

      return;
    }

    this.setState({
      status: FormStatus.FORM_LOADING
    });

    const description =
      isNexusPlatform && nexusOrder
        ? nexusRmaTemplate(
            trengoTicket.reason,
            trengoTicket.description,
            nexusOrder,
            rmaOrderItems
          )
        : order &&
          rmaTemplate(
            trengoTicket.reason,
            trengoTicket.description,
            order,
            rmaOrderItems
          );

    const uploads = await this.uploadDocuments(
      trengoTicket.document ? [trengoTicket.document] : []
    );

    try {
      await this.props.createTicket({
        variables: {
          input: {
            description,
            email: trengoTicket.email,
            orderName: trengoTicket.orderNumber,
            rmaRequest: {
              reason: trengoTicket.reason
            },
            subject: trengoTicket.subject,
            submitter: {
              name: trengoTicket.name,
              phone: trengoTicket.phone
            },
            type: TicketType.RMA_REQUEST,
            uploads,
            zip: trengoTicket.postalCode
          }
        }
      });

      this.props.history.push("/rma/thank-you");
    } catch (error) {
      this.setState({ errorCode: "network_error" });
    }
  };

  private handleChange = (changes: Partial<ITrengoTicket>) => {
    this.setState(prevState => {
      return {
        trengoTicket: { ...prevState.trengoTicket, ...changes }
      };
    });
  };

  private handlePostalCodeChange = (postalCode: string) => {
    this.setState(prevState => {
      return {
        trengoTicket: { ...prevState.trengoTicket, postalCode }
      };
    });
  };

  private getSubjectForReason(givenReason: string): Partial<ITrengoTicket> {
    const topics: Topic[] = this.props.t("inputs.subject.options", {
      returnObjects: true
    });

    const option = topics.find(([key]) => key === givenReason);

    if (option) {
      const [reason, subject] = option;

      return { subject, reason };
    } else {
      return {};
    }
  }
}

export default compose<{}, IProps>(
  withCreateTicketMutation,
  withStagedUploadsCreateMutation,
  withTranslation("rmaForm"),
  withRouter
)(RMAFormContainer);



// WEBPACK FOOTER //
// ./src/containers/RMAForm/RMAFormContainer.tsx